import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import api from '../../lib/api';
import { getManilaTodayStr, getManilaMonthStartStr } from '../../lib/dateUtils';
import PesoLayout from './PesoLayout';
import TopBar from '../../components/TopBar';
import { PESO_DEMO, PESO_DEMO_NSRP } from './pesoDemoData';
import PesoFilterBar from './PesoFilterBar';
import PesoEventsTab from './PesoEventsTab';
import PesoReportsForms from './reports/PesoReportsForms';
import GoalsTab from './goals/GoalsTab';
import { QR_API, PUBLIC_BASE_URL } from './pesoShare';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import PasswordChangeSection from '../../components/PasswordChangeSection';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Building2, UserRound, RefreshCw, LogOut, BarChart3, FileSpreadsheet, Download, Printer, Target, Briefcase, Users2, MapPin, ArrowRight, TrendingUp, Layers, Sparkles, Copy, QrCode, Link2, KeyRound, CalendarDays, Trophy } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/* ---------- PESO "Employment Facilitation" command-center widgets ---------- */
const fmtN = (n) => (n == null ? '—' : Number(n).toLocaleString());

const FUNNEL = [
  { key: 'solicited', label: 'Vacancies Solicited', text: '#cbd5e1', bar: '#64748b' },
  { key: 'registered', label: 'Applicants Registered', text: '#93c5fd', bar: '#3b82f6' },
  { key: 'referred', label: 'Applicants Referred', text: '#fbbf24', bar: '#f59e0b' },
  { key: 'placed', label: 'Applicants Placed', text: '#6ee7b7', bar: '#10b981' },
];

const FunnelHero = ({ funnel, rates }) => {
  const f = funnel || {};
  const max = Math.max(1, f.solicited || 0, f.registered || 0, f.referred || 0, f.placed || 0);
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-5 md:p-7 text-white shadow-xl">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />
      <div className="relative">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-300">Employment Facilitation</p>
            <h2 className="text-lg md:text-xl font-bold">Placement Funnel <span className="text-blue-300/80 font-medium text-sm">· lifetime</span></h2>
          </div>
          <div className="text-right">
            <div className="text-4xl md:text-5xl font-black tabular-nums text-emerald-400 leading-none">{rates?.placement_referred ?? 0}%</div>
            <div className="text-[10px] uppercase tracking-wider text-blue-200 mt-1">Placement Rate · Placed / Referred</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {FUNNEL.map((stage, i) => {
            const v = f[stage.key] || 0;
            return (
              <div key={stage.key} className="relative">
                <div className="rounded-xl bg-white/[0.06] ring-1 ring-white/10 backdrop-blur px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-blue-200 mb-1 leading-tight">{stage.label}</div>
                  <div className="text-3xl font-black tabular-nums leading-none" style={{ color: stage.text }}>{fmtN(v)}</div>
                  <div className="mt-2.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(v / max) * 100}%`, background: stage.bar }} />
                  </div>
                </div>
                {i < 3 ? <ArrowRight className="hidden md:block absolute top-1/2 -right-[11px] -translate-y-1/2 text-white/25 z-10" size={16} /> : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, accent = '#2563eb', icon: Icon }) => (
  <div className="relative bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow overflow-hidden">
    <div className="absolute top-0 left-0 h-full w-1" style={{ background: accent }} />
    <div className="p-4 pl-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
        {Icon ? <Icon size={15} style={{ color: accent }} /> : null}
      </div>
      <p className="mt-1.5 text-2xl md:text-3xl font-black text-slate-900 tabular-nums leading-none">{value}</p>
      {sub ? <p className="text-xs text-slate-500 mt-1">{sub}</p> : null}
    </div>
  </div>
);

const Panel = ({ title, hint, icon: Icon, right, children }) => (
  <Card className="shadow-md border-slate-200">
    <CardHeader className="pb-2">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-800 min-w-0">
          {Icon ? <Icon size={15} className="text-blue-700 shrink-0" /> : null}<span className="truncate">{title}</span>
        </CardTitle>
        {right || null}
      </div>
      {hint ? <p className="text-xs text-slate-400 mt-0.5">{hint}</p> : null}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const RankBars = ({ items, labelKey, valueKey, color }) => {
  if (!items || items.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No data yet.</p>;
  const max = Math.max(1, ...items.map((d) => d[valueKey] || 0));
  return (
    <div className="space-y-2.5">
      {items.map((d, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-700 font-medium truncate pr-2">{d[labelKey]}</span>
            <span className="text-slate-900 font-bold tabular-nums shrink-0">{fmtN(d[valueKey])}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(4, (d[valueKey] / max) * 100)}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ---- PESO-vs-Platform source split (labor-market "reach" charts) ----
const SOURCE_PESO = '#1d4ed8';
const SOURCE_PLATFORM = '#14b8a6';

const SourceLegend = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium">
    <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: SOURCE_PESO }} /><span className="text-slate-600">PESO-registered</span></span>
    <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: SOURCE_PLATFORM }} /><span className="text-slate-600">Platform · Bohol Trabaho</span></span>
  </div>
);

// Merge a PESO list + a platform list (each [{<labelKey>, <valueKey>}]) into
// [{ label, peso, platform }] sorted by combined total — for the source-split charts.
const mergeBySource = (pesoList, platformList, labelKey, valueKey, limit = 8) => {
  const m = new Map();
  (pesoList || []).forEach((d) => { const k = d[labelKey]; if (k != null) m.set(k, { label: k, peso: d[valueKey] || 0, platform: 0 }); });
  (platformList || []).forEach((d) => { const k = d[labelKey]; if (k == null) return; const e = m.get(k) || { label: k, peso: 0, platform: 0 }; e.platform = d[valueKey] || 0; m.set(k, e); });
  return Array.from(m.values()).sort((a, b) => (b.peso + b.platform) - (a.peso + a.platform)).slice(0, limit);
};

// Stacked PESO-vs-Platform horizontal bars (used only when platform data is present).
const StackedRankBars = ({ items }) => {
  if (!items || items.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No data yet.</p>;
  const max = Math.max(1, ...items.map((d) => (d.peso || 0) + (d.platform || 0)));
  return (
    <div className="space-y-2.5">
      {items.map((d, i) => {
        const total = (d.peso || 0) + (d.platform || 0);
        return (
          <div key={i}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-700 font-medium truncate pr-2">{d.label}</span>
              <span className="text-slate-900 font-bold tabular-nums shrink-0">{fmtN(total)}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden flex">
              {d.peso > 0 ? <div className="h-full" style={{ width: `${(d.peso / max) * 100}%`, background: SOURCE_PESO }} title={`PESO-registered: ${d.peso}`} /> : null}
              {d.platform > 0 ? <div className="h-full" style={{ width: `${(d.platform / max) * 100}%`, background: SOURCE_PLATFORM }} title={`Platform: ${d.platform}`} /> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SexSplit = ({ sex }) => {
  const m = sex?.male || 0, f = sex?.female || 0, u = sex?.unknown || 0;
  const tot = Math.max(1, m + f + u);
  const w = (n) => `${(n / tot) * 100}%`;
  return (
    <div className="py-1">
      <div className="flex h-7 rounded-full overflow-hidden ring-1 ring-slate-200">
        {m > 0 ? <div style={{ width: w(m), background: '#2563eb' }} /> : null}
        {f > 0 ? <div style={{ width: w(f), background: '#ec4899' }} /> : null}
        {u > 0 ? <div style={{ width: w(u), background: '#cbd5e1' }} /> : null}
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-blue-600" /><span className="text-slate-600">Male</span><span className="ml-auto font-bold tabular-nums text-slate-900">{fmtN(m)}</span></div>
        <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm" style={{ background: '#ec4899' }} /><span className="text-slate-600">Female</span><span className="ml-auto font-bold tabular-nums text-slate-900">{fmtN(f)}</span></div>
      </div>
    </div>
  );
};

const Coverage = ({ coverage }) => {
  const profiled = coverage?.profiled || 0;
  const total = coverage?.total || 0;
  const pct = total ? Math.round((profiled / total) * 100) : 0;
  const circ = 2 * Math.PI * 42;
  return (
    <div className="flex flex-col items-center justify-center py-1">
      <div className="relative h-28 w-28">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
          <circle cx="50" cy="50" r="42" fill="none" stroke="#1d4ed8" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * circ} ${circ}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-black text-slate-900 tabular-nums">{pct}%</span>
        </div>
      </div>
      <p className="text-sm text-slate-700 mt-2 font-semibold tabular-nums">{fmtN(profiled)} of {fmtN(total)} barangays</p>
      <p className="text-[11px] text-slate-400">have registered jobseekers</p>
    </div>
  );
};

/**
 * Isolated PESO Panglao staff dashboard (role === 'peso_staff'). PESO staff land here
 * after login and have NO seeker/employer/admin features — only /api/peso/* access:
 *   - Analytics: Panglao-scoped registration + verification/approval + vacancy stats.
 *   - Employers: approve (publishes their saved draft vacancies) / revoke; flags those
 *     due for annual (March 1) re-verification of the business permit.
 *   - Seekers: bulk "verify all pending" (they register in person at the PESO office).
 */
// Quick-share card: copy the seeker/employer registration links + download a QR image for
// each (for tarpaulins/flyers/FB posts). QR_API + PUBLIC_BASE_URL are shared via ./pesoShare
// (also reused by the Events tab), so the QR service + canonical domain live in one place.

const RegistrationShare = ({ slug }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const origin = PUBLIC_BASE_URL;
  const links = [
    { key: 'seeker', label: t('peso.shareSeeker', 'Job Seeker Registration'), url: `${origin}/peso/${slug}/seeker`, file: `PESO_${slug}_JobSeeker_Registration_QR.png` },
    { key: 'employer', label: t('peso.shareEmployer', 'Employer Registration'), url: `${origin}/peso/${slug}/employer`, file: `PESO_${slug}_Employer_Registration_QR.png` },
  ];
  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success(t('peso.linkCopied', 'Link copied')); }
    catch (e) { toast.error(t('peso.copyFailed', 'Could not copy — select the link and copy manually.')); }
  };
  const downloadQr = async (url, file, key) => {
    setBusy(key);
    try {
      const api = `${QR_API}?size=1000x1000&margin=24&format=png&data=${encodeURIComponent(url)}`;
      const res = await fetch(api);
      if (!res.ok) throw new Error('qr');
      const blob = await res.blob();
      const obj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = obj; a.download = file;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(obj), 1000);
      toast.success(t('peso.qrDownloaded', 'QR code downloaded'));
    } catch (e) {
      toast.error(t('peso.qrFailed', 'Could not generate the QR code. Please try again.'));
    } finally {
      setBusy('');
    }
  };
  return (
    <Card className="mb-5 shadow-md border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-slate-800">
          <Link2 size={15} className="text-blue-700" /> {t('peso.shareTitle', 'Share Registration Links')}
        </CardTitle>
        <p className="text-xs text-slate-400 mt-0.5">{t('peso.shareHint', 'Copy a link or download its QR code for tarpaulins, flyers, and posts.')}</p>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {links.map((l) => (
          <div key={l.key} className="flex items-center gap-2 flex-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-700">{l.label}</div>
              <div className="text-[11px] text-slate-500 truncate font-mono">{l.url}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => copy(l.url)}>
              <Copy size={14} className="mr-1" /> {t('peso.copy', 'Copy')}
            </Button>
            <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={busy === l.key} onClick={() => downloadQr(l.url, l.file, l.key)}>
              {busy === l.key ? <Loader2 size={14} className="mr-1 animate-spin" /> : <QrCode size={14} className="mr-1" />} {t('peso.downloadQr', 'QR code')}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const PesoDashboard = ({ profile, setProfile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [matches, setMatches] = useState(null);
  // Demo mode (?demo=1): render hardcoded sample data, never call the API / touch the DB.
  const [demo] = useState(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === '1');
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState('goals');
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [verifyingAll, setVerifyingAll] = useState(false);
  // Events + filters. Analytics has its OWN range/preset (default all-time so the default view
  // is unchanged); DOLE Reports keeps its month-to-date `range`. `selectedEvent` is shared.
  const [events, setEvents] = useState([]);
  // Seekers tab: client-side filter + sort (over the already-loaded list)
  const [seekerQuery, setSeekerQuery] = useState('');
  const [seekerSort, setSeekerSort] = useState('recent'); // 'recent' | 'oldest' | 'town'
  const [selectedEvent, setSelectedEvent] = useState('');
  const [aRange, setARange] = useState({ start: '', end: '' });   // Analytics window; '' = all-time
  const [aPreset, setAPreset] = useState('all');                  // highlighted preset on Analytics
  const [rPreset, setRPreset] = useState('month');                // highlighted preset on DOLE Reports

  // DOLE reporting (SPRS) state — month-to-date anchored on Asia/Manila, not the staffer's device tz.
  const [range, setRange] = useState(() => ({
    start: getManilaMonthStartStr(),
    end: getManilaTodayStr(),
  }));
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [downloading, setDownloading] = useState('');

  const authHeaders = useCallback(async () => {
    const { data: s } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${s?.session?.access_token}` };
  }, []);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    if (demo) { setSummary(PESO_DEMO.summary); setSummaryLoading(false); return; }
    try {
      const headers = await authHeaders();
      const res = await api.get('/api/peso/reports/summary', { headers, params: { start: range.start, end: range.end, ...(selectedEvent ? { event: selectedEvent } : {}) } });
      setSummary(res.data);
    } catch (e) {
      toast.error(t('peso.reportFailed', 'Failed to load report'));
    } finally {
      setSummaryLoading(false);
    }
  }, [authHeaders, range.start, range.end, selectedEvent, t, demo]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadAnalytics = useCallback(async () => {
    // NOTE: demo data is STATIC — presets/event do not re-scope it; verify on a live login / dev backend.
    if (demo) { setAnalytics(PESO_DEMO.analytics); return; }
    try {
      const headers = await authHeaders();
      const params = {};
      if (aRange.start) params.start = aRange.start;
      if (aRange.end) params.end = aRange.end;
      if (selectedEvent) params.event = selectedEvent;
      const res = await api.get('/api/peso/analytics', { headers, params });
      setAnalytics(res.data);
    } catch (e) {
      toast.error(t('peso.loadFailed', 'Failed to load PESO data'));
    }
  }, [authHeaders, aRange.start, aRange.end, selectedEvent, demo, t]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const loadEvents = useCallback(async () => {
    if (demo) { setEvents(PESO_DEMO.events || []); return; }
    try {
      const headers = await authHeaders();
      const res = await api.get('/api/peso/events', { headers });
      setEvents(res.data?.events || []);
    } catch (e) { /* non-fatal: events are optional */ }
  }, [authHeaders, demo]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ---- Demo-mode (?demo=1) helpers: client-side CSV export + NSRP preview, never touch the DB ----
  const csvEsc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadDemoCsv = (type) => {
    const ins = PESO_DEMO.analytics.insights;
    let rows = [];
    if (type === 'registrations') {
      rows = PESO_DEMO.registrations.seekers.map((s) => ({ Name: s.name, Town: s.town, 'ID Verified': s.verified ? 'Yes' : 'No', 'Registered At': s.created_at }));
    } else if (type === 'vacancies') {
      rows = ins.in_demand.map((v) => ({ 'Job Title': v.title, Vacancies: v.vacancies }));
    } else if (type === 'referrals') {
      rows = PESO_DEMO.matches.matches.filter((m) => m.best).map((m) => ({ Jobseeker: m.name, Town: m.town, 'Recommended Vacancy': m.best.title, Employer: m.best.employer }));
    } else {
      rows = PESO_DEMO.matches.matches.filter((m) => m.best).slice(0, PESO_DEMO.summary.placed.seekers).map((m) => ({ Jobseeker: m.name, Vacancy: m.best.title, Employer: m.best.employer }));
    }
    const cols = rows.length ? Object.keys(rows[0]) : ['No data'];
    const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => csvEsc(r[c])).join(','))].join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `PESO_${type}_demo.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Demo: ${type} CSV downloaded (${rows.length} rows)`);
  };
  const printNsrpDemo = async (s) => {
    try {
      const parts = (s.name || 'Juan Dela Cruz').trim().split(/\s+/);
      const surname = parts.length > 1 ? parts[parts.length - 1] : parts[0];
      const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
      const base = PESO_DEMO_NSRP;
      const payload = {
        ...base,
        name: s.name || base.name,
        town: s.town || base.town,
        nsrp_data: {
          ...base.nsrp_data,
          personal: { ...base.nsrp_data.personal, surname, first_name: first, barangay: s.town || base.nsrp_data.personal.barangay },
        },
      };
      const headers = await authHeaders();
      const res = await api.post('/api/peso/nsrp/preview', payload, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not render the sample NSRP');
    }
  };

  const printNsrp2Demo = async (e) => {
    try {
      const payload = {
        business_name: e.name || 'Panglao Beach Resort Inc.',
        town: e.town || 'Panglao',
        nsrp_data: {
          form: 'nsrp_form_2',
          establishment: {
            name: e.name || 'Panglao Beach Resort Inc.', acronym: 'PBR', tin: '123-456-789-000',
            employer_type: 'Private', work_force: 'Small (10-99)',
            line_of_business: 'Accommodation and Food Service Activities', address: '123 Beachfront Road',
            barangay: 'Tawala', municipality: e.town || 'Panglao', province: 'Bohol',
          },
          contact: {
            title: 'Ms.', contact_person: 'Maria Santos', position: 'HR Manager',
            telephone: '038-000-1111', mobile: '09171234567', fax: '', email: 'hr@panglaobeach.com',
          },
          vacancy: {
            position_title: 'Front Desk Officer',
            job_description: 'Handle guest check-in and check-out, manage reservations, and coordinate with housekeeping.',
            nature_of_work: 'Permanent', place_of_work: e.town || 'Panglao', salary_min: '15000',
            salary_max: '18000', salary_note: 'plus service charge', vacancy_count: '2',
            panglao_category: 'Tourism & Hospitality Services',
          },
          requirements: {
            work_experience_months: '12', religion: 'Any', sex: 'No Preference', civil_status: 'No Preference',
            other_qualifications: 'Pleasing personality; willing to work weekends and holidays.',
            accepts_disability: 'yes', accepts_disability_types: ['Hearing'], educational_level: 'College Graduate',
            course_major: 'Hospitality Management', license: 'N/A', eligibility: 'N/A',
            certification: 'NC II Front Office', languages_spoken: 'English, Cebuano', preferred_residence: 'Panglao',
            accepts_programs: ['PESO', 'SPES'],
          },
          posting: { posting_date: '2026-07-02', valid_until: '2026-08-31' },
        },
      };
      const headers = await authHeaders();
      const res = await api.post('/api/peso/nsrp2/preview', payload, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Could not render the sample NSRP Form 2');
    }
  };

  const downloadReport = async (type) => {
    if (demo) { downloadDemoCsv(type); return; }
    setDownloading(type);
    try {
      const headers = await authHeaders();
      const res = await api.get('/api/peso/reports/export', {
        headers, params: { type, start: range.start, end: range.end, ...(selectedEvent ? { event: selectedEvent } : {}) }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PESO_${type}_${range.start}_to_${range.end}${selectedEvent ? `_${selectedEvent}` : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('peso.exportFailed', 'Export failed'));
    } finally {
      setDownloading('');
    }
  };

  const load = useCallback(async (quiet = false) => {
    // `quiet !== true` so an event object (Refresh button onClick={load}) still shows the
    // full loader; only an explicit load(true) from an in-place action refreshes silently.
    if (quiet !== true) setLoading(true);
    if (demo) {
      setData(PESO_DEMO.registrations);
      setMatches(PESO_DEMO.matches);
      setLoading(false);
      return;
    }
    try {
      const headers = await authHeaders();
      const [regs, mtch] = await Promise.all([
        api.get('/api/peso/registrations', { headers }),
        api.get('/api/peso/matches', { headers }),
      ]);
      setData(regs.data);
      setMatches(mtch.data);
    } catch (e) {
      toast.error(t('peso.loadFailed', 'Failed to load PESO data'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, t, demo]);

  useEffect(() => { load(); }, [load]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const approveEmployer = async (employer_id, approved) => {
    if (demo) {
      setData((d) => ({ ...d, employers: (d.employers || []).map((e) => e.id === employer_id ? { ...e, peso_approved: approved, renewal_due: false } : e) }));
      toast.success(approved ? 'Approved (demo)' : 'Approval revoked (demo)');
      return;
    }
    setBusyId(employer_id);
    try {
      const headers = await authHeaders();
      const res = await api.post('/api/peso/approve-employer', { employer_id, approved }, { headers });
      toast.success(approved
        ? t('peso.approvedPublished', { count: res.data.published || 0, defaultValue: `Approved — ${res.data.published || 0} vacancy(ies) published` })
        : t('peso.approvalRevoked', 'Approval revoked'));
      if (approved && res.data.held_for_salary > 0) {
        toast.warning(t('peso.heldForSalary', { count: res.data.held_for_salary, defaultValue: `${res.data.held_for_salary} draft(s) were NOT published because they have no valid salary — the employer must add a salary before they can go live.` }));
      }
      await load(true);
      loadAnalytics(); // analytics is fetched separately now — refresh its counts after in-place actions
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('peso.actionFailed', 'Action failed'));
    } finally {
      setBusyId(null);
    }
  };

  const verifyAllSeekers = async () => {
    if (demo) {
      setData((d) => ({ ...d, seekers: (d.seekers || []).map((s) => ({ ...s, verified: true })), pending_seekers: 0 }));
      toast.success('Verified all pending (demo)');
      return;
    }
    setVerifyingAll(true);
    try {
      const headers = await authHeaders();
      const res = await api.post('/api/peso/verify-seekers', { all_pending: true }, { headers });
      toast.success(t('peso.verifiedCount', { count: res.data.verified || 0, defaultValue: `Verified ${res.data.verified || 0} PESO seeker(s)` }));
      await load(true);
      loadAnalytics(); // analytics is fetched separately now — refresh its counts after in-place actions
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('peso.verifyFailed', 'Verification failed'));
    } finally {
      setVerifyingAll(false);
    }
  };

  const verifySeeker = async (seeker_id) => {
    if (demo) {
      setData((d) => ({ ...d, seekers: (d.seekers || []).map((s) => s.id === seeker_id ? { ...s, verified: true } : s), pending_seekers: Math.max(0, (d.pending_seekers || 0) - 1) }));
      toast.success('Seeker verified (demo)');
      return;
    }
    setBusyId(seeker_id);
    try {
      const headers = await authHeaders();
      await api.post('/api/peso/verify-seekers', { seeker_ids: [seeker_id] }, { headers });
      toast.success(t('peso.seekerVerified', 'Seeker verified'));
      await load(true);
      loadAnalytics(); // analytics is fetched separately now — refresh its counts after in-place actions
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('peso.verifyFailed', 'Verification failed'));
    } finally {
      setBusyId(null);
    }
  };

  const employers = data?.employers || [];
  const seekers = data?.seekers || [];
  const pendingSeekers = data?.pending_seekers || 0;

  // Filter + sort the seekers list client-side (over the already-loaded rows) so staff can
  // sort by registration time / hometown and export exactly what they're looking at.
  const filteredSeekers = (() => {
    const q = seekerQuery.trim().toLowerCase();
    const list = seekers.filter((s) =>
      !q || (s.name || '').toLowerCase().includes(q) || (s.town || '').toLowerCase().includes(q)
    );
    return [...list].sort((a, b) => {
      if (seekerSort === 'town') return (a.town || '').localeCompare(b.town || '');
      const ta = a.created_at || '', tb = b.created_at || '';
      return seekerSort === 'oldest' ? ta.localeCompare(tb) : tb.localeCompare(ta);
    });
  })();

  const exportSeekersCsv = () => {
    const cols = ['Name', 'Town', 'ID Verified', 'Registered At'];
    const rows = filteredSeekers.map((s) => [s.name || '', s.town || '', s.verified ? 'Yes' : 'No', s.created_at || '']);
    const csv = [cols.join(','), ...rows.map((r) => r.map(csvEsc).join(','))].join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `PESO_jobseekers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} jobseeker${rows.length === 1 ? '' : 's'}`);
  };

  const Stat = ({ label, value, sub, tone }) => (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${tone || ''}`}>{value}</p>
        {sub != null && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );

  // Shared renderers so the desktop table and the mobile card layout stay in lockstep
  // (identical status/action logic — change once, both surfaces update). h-9 lifts the
  // action buttons off the 32px (size="sm") default toward a comfier mobile tap target.
  const employerStatusBadge = (e) => (
    e.renewal_due ? <Badge variant="destructive">{t('peso.badgeRenewal', 'Renewal due')}</Badge>
      : e.peso_approved ? <Badge className="bg-green-500">{t('peso.badgeApproved', 'Approved')}</Badge>
      : <Badge variant="secondary">{t('peso.badgePending', 'Pending')}</Badge>
  );
  const employerActions = (e) => (
    <>
      <Button size="sm" variant="ghost" className="h-9" onClick={() => demo ? printNsrp2Demo(e) : window.open(`/peso/nsrp2/${e.id}`, '_blank')} title={t('peso.printNsrp2', 'Print NSRP Form 2')}>
        <Printer size={14} className="mr-1" /> {t('peso.printNsrp2', 'Print Form 2')}
      </Button>
      {(e.peso_approved && !e.renewal_due) ? (
        <Button size="sm" variant="outline" className="h-9" disabled={busyId === e.id} onClick={() => approveEmployer(e.id, false)}>
          {busyId === e.id ? <Loader2 size={14} className="animate-spin" /> : t('peso.btnRevoke', 'Revoke')}
        </Button>
      ) : (
        <Button size="sm" className="h-9 bg-blue-700 hover:bg-blue-800" disabled={busyId === e.id} onClick={() => approveEmployer(e.id, true)}>
          {busyId === e.id ? <Loader2 size={14} className="animate-spin" /> : (e.renewal_due ? t('peso.btnReapprove', 'Re-approve') : t('peso.btnApprove', 'Approve'))}
        </Button>
      )}
    </>
  );
  const seekerActions = (s) => (
    <>
      <Button size="sm" variant="ghost" className="h-9" onClick={() => demo ? printNsrpDemo(s) : window.open(`/peso/nsrp/${s.id}`, '_blank')} title={t('peso.printNsrp', 'Print NSRP form')}>
        <Printer size={14} className="mr-1" /> {t('peso.printNsrp', 'Print NSRP')}
      </Button>
      {!s.verified && (
        <Button size="sm" variant="outline" className="h-9" disabled={busyId === s.id} onClick={() => verifySeeker(s.id)}>
          {busyId === s.id ? <Loader2 size={14} className="animate-spin" /> : t('peso.btnVerify', 'Verify')}
        </Button>
      )}
    </>
  );
  // Match strength from the AI score (0-100) the seeker's own recommendation carries.
  // The previous thresholds (>=12 Strong) were read against a shortlisting heuristic in
  // which living in the same town was worth 10 points and skills nothing — so a bartender
  // showed "Strong" for an Academic Supervisor vacancy. Never label strength from a recall
  // score again; this reads the judged score, and the evidence sits beside it either way.
  const matchTier = (m) => {
    const score = m.best?.score;
    if (score == null) return null;
    return score >= 80 ? { label: 'Strong', cls: 'bg-emerald-500' }
      : score >= 60 ? { label: 'Good', cls: 'bg-blue-600' }
      : { label: 'Possible', cls: 'bg-amber-500' };
  };

  // What we can actually evidence about this jobseeker, for the officer to check the
  // AI's sentence against. Recorded skills first, then the NSRP intake, then work history.
  const evidenceChips = (m) => {
    const ev = m.evidence || {};
    return [
      ...(ev.skills || []).slice(0, 4).map((s) => ({ t: s, k: 'skill' })),
      ...(ev.other_skills || []).slice(0, 3).map((s) => ({ t: s, k: 'nsrp' })),
      ...(ev.work_titles || []).slice(0, 2).map((s) => ({ t: s, k: 'work' })),
    ];
  };
  const CHIP_CLS = {
    skill: 'bg-slate-100 text-slate-600',
    nsrp: 'bg-indigo-50 text-indigo-700',
    work: 'bg-teal-50 text-teal-700',
  };

  return (
    <>
      <TopBar profile={profile} setProfile={setProfile} />
      <PesoLayout wide>
      <div className="mb-5 rounded-2xl bg-white border border-slate-200 shadow-sm px-4 py-3.5 md:px-5 md:py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-700 to-blue-900 text-white grid place-items-center shrink-0 shadow-sm">
            <Building2 size={20} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700/80">DOLE Region VII · Employment Facilitation</p>
            <h1 className="text-lg md:text-2xl font-bold text-slate-900 leading-tight truncate">
              {analytics?.office?.name || 'PESO Panglao'}
              <span className="text-slate-300 font-normal mx-1.5">/</span>
              <span className="text-slate-500 font-semibold text-base md:text-xl">{t('peso.dashTitle', 'Staff Dashboard')}</span>
            </h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" className="h-9" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('peso.refresh', 'Refresh')}
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setShowPwDialog(true)}>
            <KeyRound size={14} className="mr-1" /> {t('peso.changePassword', 'Change password')}
          </Button>
          <Button variant="ghost" size="sm" className="h-9 ml-auto sm:ml-0" onClick={signOut}>
            <LogOut size={14} className="mr-1" /> {t('peso.signOut', 'Sign out')}
          </Button>
        </div>
      </div>

      <RegistrationShare slug={profile?.peso_office || analytics?.office?.slug || 'panglao'} />

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 animate-spin" /> {t('peso.loading', 'Loading…')}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-7 h-auto md:h-9 gap-1 md:gap-0">
            <TabsTrigger value="goals" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><Trophy size={14} className="mr-1 shrink-0" /> {t('peso.tabGoals', 'Goals')}</TabsTrigger>
            <TabsTrigger value="analytics" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><BarChart3 size={14} className="mr-1 shrink-0" /> {t('peso.tabAnalytics', 'Analytics')}</TabsTrigger>
            <TabsTrigger value="matching" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><Sparkles size={14} className="mr-1 shrink-0" /> {t('peso.tabMatching', 'Job Matching')}</TabsTrigger>
            <TabsTrigger value="employers" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><Building2 size={14} className="mr-1 shrink-0" /> {t('peso.tabEmployers', 'Employers')}</TabsTrigger>
            <TabsTrigger value="seekers" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><UserRound size={14} className="mr-1 shrink-0" /> {t('peso.tabSeekers', 'Job Seekers')}</TabsTrigger>
            <TabsTrigger value="reports" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><FileSpreadsheet size={14} className="mr-1 shrink-0" /> {t('peso.tabReports', 'DOLE Reports')}</TabsTrigger>
            <TabsTrigger value="events" className="text-xs sm:text-sm px-1.5 md:px-3 py-2.5 md:py-1 min-h-[44px] md:min-h-0 h-auto whitespace-normal md:whitespace-nowrap leading-tight"><CalendarDays size={14} className="mr-1 shrink-0" /> {t('peso.tabEvents', 'Events')}</TabsTrigger>
          </TabsList>

          {/* ---------------- Goals (Best-PESO scoreboard; the office's home tab) ---------------- */}
          <TabsContent value="goals" className="space-y-4">
            {demo
              ? <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t('peso.goalsDemoNote', 'The Best-PESO scoreboard opens with your staff sign-in.')}</CardContent></Card>
              : <GoalsTab authHeaders={authHeaders} />}
          </TabsContent>

          {/* ---------------- Job Matching (core PESO function: refer seekers to vacancies) ---------------- */}
          <TabsContent value="matching" className="space-y-4">
            {matches ? (
              <>
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-5 md:p-6 text-white shadow-xl">
                  <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />
                  <div className="relative flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-300">Jobs-Skills Matching</p>
                      <h2 className="text-lg md:text-xl font-bold">Recommended Referrals</h2>
                      <p className="text-sm text-blue-200/90 mt-0.5 max-w-xl">Each registered jobseeker's best currently-open vacancy across Bohol, from their own AI job matches. Check the recorded evidence before referring.</p>
                    </div>
                    <div className="flex gap-6 text-right shrink-0">
                      <div><div className="text-3xl font-black tabular-nums text-emerald-400 leading-none">{fmtN(matches.matched)}</div><div className="text-[10px] uppercase tracking-wider text-blue-200 mt-1">Seekers matched</div></div>
                      <div><div className="text-3xl font-black tabular-nums text-amber-400 leading-none">{fmtN(matches.open_vacancies)}</div><div className="text-[10px] uppercase tracking-wider text-blue-200 mt-1">Open vacancies</div></div>
                    </div>
                  </div>
                </div>

                <Panel title="Jobseeker → Best Open Vacancy" hint="Sorted by match score. The AI note is a suggestion, not an assessment by your office — confirm against the recorded evidence and interview before referring." icon={Sparkles}>
                  {(matches.matches || []).length === 0 ? (
                    <p className="text-sm text-slate-400 py-8 text-center">No registered jobseekers yet.</p>
                  ) : (
                    <>
                    {/* Mobile: stacked cards — no horizontal scroll */}
                    <div className="md:hidden space-y-3">
                      {matches.matches.map((m) => {
                        const tier = matchTier(m);
                        return (
                          <div key={m.seeker_id} className="rounded-xl border border-slate-200 p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                                  <span className="truncate">{m.name}</span>
                                  {m.verified ? <ShieldCheck size={13} className="text-emerald-600 shrink-0" /> : null}
                                </div>
                                <div className="text-xs text-slate-400 truncate">{m.town || '—'}{m.verified ? '' : ' · unverified'}</div>
                              </div>
                              {tier ? <span className={`shrink-0 inline-block text-white text-[11px] font-bold rounded-full px-2.5 py-1 ${tier.cls}`}>{tier.label}</span> : null}
                            </div>
                            {evidenceChips(m).length ? (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {evidenceChips(m).map((c, i) => (
                                  <span key={i} className={`text-[11px] rounded px-1.5 py-0.5 ${CHIP_CLS[c.k]}`}>{c.t}</span>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 inline-block">
                                no skills or work history recorded
                              </div>
                            )}
                            <div className="mt-2 rounded-lg bg-slate-50 p-2">
                              {m.best ? (
                                <>
                                  <div className="font-medium text-slate-800">{m.best.title}</div>
                                  {m.best.reason ? (
                                    <div className="mt-1.5 text-[11px] text-slate-500 border-l-2 border-slate-200 pl-2 italic">
                                      <span className="not-italic font-semibold text-slate-400">AI note{m.best.evidence_status === 'verified' ? '' : ' (unverified)'}: </span>
                                      {m.best.reason}
                                    </div>
                                  ) : m.best.reason_withheld ? (
                                    <div className="mt-1.5 text-[11px] text-amber-700">
                                      AI note withheld — it asserted experience this profile does not record.
                                    </div>
                                  ) : null}
                                  <div className="text-xs text-slate-500">
                                    {m.best.employer}
                                    {m.best.source ? (m.best.source === 'peso'
                                      ? <span className="ml-1.5 inline-block text-[10px] font-semibold rounded px-1.5 py-0.5 bg-blue-100 text-blue-700">PESO</span>
                                      : <span className="ml-1.5 inline-block text-[10px] font-semibold rounded px-1.5 py-0.5 bg-teal-100 text-teal-700">Platform</span>) : null}
                                  </div>
                                  {(m.others || []).length ? (
                                    <div className="text-[11px] text-slate-400 mt-0.5">also: {m.others.map((o) => o.title).join(', ')}</div>
                                  ) : null}
                                </>
                              ) : <span className="text-sm text-slate-400">No matching open vacancy</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Desktop: full table */}
                    <div className="hidden md:block">
                    <Table className="min-w-[640px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Job Seeker</TableHead>
                          <TableHead>Recorded evidence</TableHead>
                          <TableHead>Suggested vacancy &amp; AI note</TableHead>
                          <TableHead className="text-right">Match</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matches.matches.map((m) => {
                          const tier = matchTier(m);
                          return (
                            <TableRow key={m.seeker_id}>
                              <TableCell>
                                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                                  {m.name}
                                  {m.verified ? <ShieldCheck size={13} className="text-emerald-600" /> : null}
                                </div>
                                <div className="text-xs text-slate-400">{m.town || '—'}{m.verified ? '' : ' · unverified'}</div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {evidenceChips(m).length === 0 ? (
                                    <span className="text-xs text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                                      no skills or work history recorded
                                    </span>
                                  ) : evidenceChips(m).map((c, i) => (
                                    <span key={i} className={`text-[11px] rounded px-1.5 py-0.5 ${CHIP_CLS[c.k]}`}>{c.t}</span>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                {m.best ? (
                                  <>
                                    <div className="font-medium text-slate-800">{m.best.title}</div>
                                    <div className="text-xs text-slate-500">
                                      {m.best.employer}
                                      {m.best.source ? (m.best.source === 'peso'
                                        ? <span className="ml-1.5 inline-block text-[10px] font-semibold rounded px-1.5 py-0.5 bg-blue-100 text-blue-700">PESO</span>
                                        : <span className="ml-1.5 inline-block text-[10px] font-semibold rounded px-1.5 py-0.5 bg-teal-100 text-teal-700">Platform</span>) : null}
                                    </div>
                                    {m.best.reason ? (
                                      <div className="mt-1.5 text-[11px] text-slate-500 border-l-2 border-slate-200 pl-2 italic">
                                        <span className="not-italic font-semibold text-slate-400">
                                          AI note{m.best.evidence_status === 'verified' ? ' (cited profile facts)' : ' (unverified)'}:{' '}
                                        </span>
                                        {m.best.reason}
                                      </div>
                                    ) : m.best.reason_withheld ? (
                                      <div className="mt-1.5 text-[11px] text-amber-700">
                                        AI note withheld — it asserted experience this profile does not record.
                                      </div>
                                    ) : null}
                                    {(m.others || []).length ? (
                                      <div className="text-[11px] text-slate-400 mt-0.5">also: {m.others.map((o) => o.title).join(', ')}</div>
                                    ) : null}
                                  </>
                                ) : (
                                  <span className="text-sm text-slate-400">
                                    {m.status === 'not_analyzed' ? 'Not analysed yet' : 'No open vacancy matched'}
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {tier ? <span className={`inline-block text-white text-[11px] font-bold rounded-full px-2.5 py-1 ${tier.cls}`}>{tier.label}</span>
                                  : <span className="text-xs text-slate-300">—</span>}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                    </>
                  )}
                </Panel>
              </>
            ) : (
              <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="mr-2 animate-spin" /> Matching…</div>
            )}
          </TabsContent>

          {/* ---------------- Analytics (PESO Employment Facilitation) ---------------- */}
          <TabsContent value="analytics" className="space-y-5">
            <PesoFilterBar range={aRange} onRangeChange={setARange} activePreset={aPreset} onPresetChange={setAPreset}
              events={events} selectedEvent={selectedEvent} onEventChange={setSelectedEvent} showEvents={events.length > 0} />
            {analytics ? (() => {
              const ins = analytics.insights || {};
              const r = ins.rates || {};
              // Organic/platform Panglao layer (partnership "reach" — labeled, never in DOLE reports).
              // Only present in demo today; the live API omits it, so the dashboard stays PESO-only there.
              const platform = analytics.platform || null;
              const reachSeekers = (analytics.seekers?.total || 0) + (platform?.seekers || 0);
              const reachEmployers = (analytics.employers?.total || 0) + (platform?.employers || 0);
              const reachVacancies = (analytics.vacancies?.open || 0) + (platform?.open_vacancies || 0);
              const inDemandMerged = platform ? mergeBySource(ins.in_demand, ins.in_demand_platform, 'title', 'vacancies') : null;
              const supplyMerged = platform ? mergeBySource(ins.applicant_supply, ins.applicant_supply_platform, 'name', 'count') : null;
              const employersMerged = platform ? mergeBySource(ins.top_employers, ins.top_employers_platform, 'name', 'vacancies') : null;
              const townsMerged = platform ? mergeBySource(analytics.towns, analytics.towns_platform, 'town', 'count', 12) : null;
              return (
                <>
                  <FunnelHero funnel={ins.funnel} rates={r} />

                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <Kpi label="Placement Rate" value={`${r.placement_referred ?? 0}%`} sub="Placed / Referred" accent="#059669" icon={Target} />
                    <Kpi label="True Reach" value={`${r.placement_registered ?? 0}%`} sub="Placed / Registered" accent="#2563eb" icon={TrendingUp} />
                    <Kpi label="Vacancy Fill Rate" value={`${r.fill_rate ?? 0}%`} sub="Placed / Solicited" accent="#d97706" icon={Layers} />
                    <Kpi label="Open Vacancies" value={fmtN(analytics.vacancies?.open ?? 0)} sub={`${fmtN(analytics.vacancies?.pending_drafts ?? 0)} pending drafts`} accent="#0ea5e9" icon={Briefcase} />
                    <Kpi label="Renewal Due" value={fmtN(analytics.employers?.renewal_due ?? 0)} sub="Re-check permit" accent={(analytics.employers?.renewal_due ?? 0) > 0 ? '#dc2626' : '#64748b'} icon={ShieldCheck} />
                  </div>

                  {platform ? (
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-800 via-teal-900 to-slate-900 p-5 md:p-6 text-white shadow-xl">
                      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />
                      <div className="relative">
                        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                          <div>
                            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-300">Panglao Labor Market</p>
                            <h2 className="text-lg md:text-xl font-bold">Full-market reach <span className="text-teal-300/80 font-medium text-sm">· shared by Bohol Trabaho</span></h2>
                            <p className="text-xs text-teal-200/80 mt-0.5 max-w-xl">Beyond your PESO registrations — the whole Panglao talent &amp; vacancy pool on the platform. Shared as a partnership benefit; not part of your DOLE reports.</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-white/90" /><span className="text-teal-100">PESO-registered</span></span>
                            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: SOURCE_PLATFORM }} /><span className="text-teal-100">Platform</span></span>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {[
                            { label: 'Job Seekers in Panglao', total: reachSeekers, peso: analytics.seekers?.total || 0, plat: platform.seekers },
                            { label: 'Employers in Panglao', total: reachEmployers, peso: analytics.employers?.total || 0, plat: platform.employers },
                            { label: 'Open Vacancies in Panglao', total: reachVacancies, peso: analytics.vacancies?.open || 0, plat: platform.open_vacancies },
                          ].map((k) => (
                            <div key={k.label} className="rounded-xl bg-white/[0.06] ring-1 ring-white/10 backdrop-blur px-4 py-3">
                              <div className="text-[10px] uppercase tracking-wider text-teal-200 mb-1 leading-tight">{k.label}</div>
                              <div className="text-3xl md:text-4xl font-black tabular-nums leading-none">{fmtN(k.total)}</div>
                              <div className="mt-1.5 text-[11px] text-teal-200/90 tabular-nums">{fmtN(k.peso)} PESO · {fmtN(k.plat)} platform</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="grid md:grid-cols-3 gap-4">
                    <Panel title="Registrants by Sex" hint="Sex-disaggregated (GAD compliance)" icon={Users2}>
                      <SexSplit sex={ins.sex} />
                    </Panel>
                    <Panel title="Registrants by Age Band" hint="DOLE standard age bands" icon={BarChart3}>
                      {(ins.age_bands || []).some((b) => b.count > 0) ? (
                        <ResponsiveContainer width="100%" height={172}>
                          <BarChart data={ins.age_bands}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="band" fontSize={10} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} fontSize={10} width={24} axisLine={false} tickLine={false} />
                            <Tooltip />
                            <Bar dataKey="count" name="Registrants" fill="#2563eb" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-slate-400 py-10 text-center">No data yet.</p>}
                    </Panel>
                    <Panel title="Skills Registry Coverage" hint="Barangays profiled (NSRP)" icon={MapPin}>
                      <Coverage coverage={ins.coverage} />
                    </Panel>
                  </div>

                  <Panel title="Priority Sectors Served" hint="Special clients under RA 10691 (sex- &amp; group-disaggregated)" icon={ShieldCheck}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {(ins.special_groups || []).map((g) => (
                        <div key={g.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                          <div className="text-2xl font-black tabular-nums text-slate-900">{fmtN(g.count)}</div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mt-0.5">{g.label}</div>
                        </div>
                      ))}
                    </div>
                  </Panel>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Panel title="In-Demand Occupations" hint={platform ? 'Open Panglao vacancies, by position' : 'Vacancies solicited, by position'} icon={Briefcase} right={platform ? <SourceLegend /> : null}>
                      {inDemandMerged ? <StackedRankBars items={inDemandMerged} /> : <RankBars items={ins.in_demand || []} labelKey="title" valueKey="vacancies" color="#d97706" />}
                    </Panel>
                    <Panel title="Applicant Skills Supply" hint={platform ? 'Jobseeker skills across Panglao — jobs-skills matching' : 'Registered jobseeker skills — jobs-skills matching'} icon={UserRound} right={platform ? <SourceLegend /> : null}>
                      {supplyMerged ? <StackedRankBars items={supplyMerged} /> : <RankBars items={ins.applicant_supply || []} labelKey="name" valueKey="count" color="#2563eb" />}
                    </Panel>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Panel title="Top Hiring Employers" hint="By open vacancies offered" icon={Building2} right={platform ? <SourceLegend /> : null}>
                      {employersMerged ? <StackedRankBars items={employersMerged} /> : <RankBars items={ins.top_employers || []} labelKey="name" valueKey="vacancies" color="#059669" />}
                    </Panel>
                    <Panel title="Registrations Trend" hint="Last 12 months" icon={TrendingUp}>
                      {(analytics.trend || []).length === 0 ? (
                        <p className="text-sm text-slate-400 py-10 text-center">No data yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={analytics.trend}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" fontSize={10} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} fontSize={10} width={24} axisLine={false} tickLine={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="seekers" name="PESO Seekers" stroke="#2563eb" strokeWidth={2.5} dot={false} />
                            <Line type="monotone" dataKey="employers" name="PESO Employers" stroke="#059669" strokeWidth={2.5} dot={false} />
                            {platform ? <Line type="monotone" dataKey="seekers_platform" name="Platform Seekers" stroke={SOURCE_PLATFORM} strokeWidth={2.5} strokeDasharray="4 3" dot={false} /> : null}
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </Panel>
                  </div>

                  <Panel title="Registrants by Barangay" hint={platform ? 'Talent pool by barangay — PESO + platform' : 'Skills registry — top towns / barangays'} icon={MapPin} right={platform ? <SourceLegend /> : null}>
                    {(() => {
                      const data = townsMerged || analytics.towns || [];
                      if (data.length === 0) return <p className="text-sm text-slate-400 py-10 text-center">No data yet.</p>;
                      return (
                        <ResponsiveContainer width="100%" height={Math.max(160, data.length * 28)}>
                          <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} fontSize={10} axisLine={false} tickLine={false} />
                            <YAxis type="category" dataKey={townsMerged ? 'label' : 'town'} width={84} fontSize={11} axisLine={false} tickLine={false} />
                            <Tooltip />
                            {townsMerged ? (
                              <>
                                <Bar dataKey="peso" name="PESO-registered" stackId="brgy" fill={SOURCE_PESO} />
                                <Bar dataKey="platform" name="Platform" stackId="brgy" fill={SOURCE_PLATFORM} radius={[0, 4, 4, 0]} />
                              </>
                            ) : (
                              <Bar dataKey="count" name="Registrants" fill="#1d4ed8" radius={[0, 4, 4, 0]} />
                            )}
                          </BarChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </Panel>
                </>
              );
            })() : (
              <div className="py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={18} /></div>
            )}
          </TabsContent>

          {/* ---------------- Employers ---------------- */}
          <TabsContent value="employers">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Building2 className="h-4 w-4" /> {t('peso.tabEmployers', 'Employers')} ({employers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {employers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('peso.noEmployers', 'No PESO employer registrations yet.')}</p>
                ) : (
                  <>
                    {/* Mobile: stacked cards — status + actions always visible, no horizontal scroll */}
                    <div className="md:hidden space-y-3">
                      {employers.map((e) => (
                        <div key={e.id} className="rounded-xl border border-slate-200 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 truncate">{e.name || '—'}</div>
                              <div className="text-xs text-slate-500 truncate">{[e.town, e.phone].filter(Boolean).join(' · ') || '—'}</div>
                            </div>
                            <div className="shrink-0">{employerStatusBadge(e)}</div>
                          </div>
                          <div className="mt-1 text-xs text-slate-500">{t('peso.colDrafts', 'Pending drafts')}: {e.pending_drafts || 0}</div>
                          <div className="mt-2 flex flex-wrap gap-2">{employerActions(e)}</div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop: full table (scrolls horizontally inside its wrapper if needed) */}
                    <div className="hidden md:block">
                      <Table className="min-w-[720px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('peso.colBusiness', 'Business')}</TableHead>
                            <TableHead>{t('peso.colTown', 'Town')}</TableHead>
                            <TableHead>{t('peso.colPhone', 'Phone')}</TableHead>
                            <TableHead className="text-right">{t('peso.colDrafts', 'Pending drafts')}</TableHead>
                            <TableHead>{t('peso.colStatus', 'Status')}</TableHead>
                            <TableHead className="text-right">{t('peso.colAction', 'Action')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {employers.map((e) => (
                            <TableRow key={e.id}>
                              <TableCell className="font-medium">{e.name || '—'}</TableCell>
                              <TableCell>{e.town || '—'}</TableCell>
                              <TableCell>{e.phone || '—'}</TableCell>
                              <TableCell className="text-right">{e.pending_drafts || 0}</TableCell>
                              <TableCell>{employerStatusBadge(e)}</TableCell>
                              <TableCell className="text-right space-x-1 whitespace-nowrap">{employerActions(e)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------------- Seekers ---------------- */}
          <TabsContent value="seekers">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2"><UserRound className="h-4 w-4" /> {t('peso.tabSeekers', 'Job Seekers')} ({filteredSeekers.length}{filteredSeekers.length !== seekers.length ? ` of ${seekers.length}` : ''}) — {t('peso.pendingCount', { count: pendingSeekers, defaultValue: `${pendingSeekers} pending verification` })}</span>
                  <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={verifyingAll || pendingSeekers === 0} onClick={verifyAllSeekers}>
                    {verifyingAll ? <><Loader2 size={14} className="mr-1 animate-spin" /> {t('peso.verifying', 'Verifying…')}</> : <><ShieldCheck size={14} className="mr-1" /> {t('peso.verifyAll', 'Verify all pending')}</>}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {seekers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('peso.noSeekers', 'No PESO seeker registrations yet.')}</p>
                ) : (
                  <>
                    {/* Filter + sort + export the (already-loaded) jobseekers list */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <input
                        value={seekerQuery}
                        onChange={(e) => setSeekerQuery(e.target.value)}
                        placeholder={t('peso.searchSeekers', 'Search name or town…')}
                        className="h-9 px-3 rounded-md border border-slate-300 text-sm flex-1 min-w-[160px]"
                      />
                      <select
                        value={seekerSort}
                        onChange={(e) => setSeekerSort(e.target.value)}
                        className="h-9 px-2 rounded-md border border-slate-300 text-sm bg-white"
                        aria-label={t('peso.sortSeekers', 'Sort jobseekers')}
                      >
                        <option value="recent">{t('peso.sortRecent', 'Newest first')}</option>
                        <option value="oldest">{t('peso.sortOldest', 'Oldest first')}</option>
                        <option value="town">{t('peso.sortTown', 'Town (A–Z)')}</option>
                      </select>
                      <Button size="sm" variant="outline" onClick={exportSeekersCsv} disabled={filteredSeekers.length === 0}>
                        {t('peso.exportCsv', 'Export CSV')}
                      </Button>
                    </div>
                    {filteredSeekers.length === 0 && (
                      <p className="text-sm text-muted-foreground">{t('peso.noSeekerMatch', 'No jobseekers match your filter.')}</p>
                    )}
                    {/* Mobile: stacked cards — status + actions always visible, no horizontal scroll */}
                    <div className="md:hidden space-y-3">
                      {filteredSeekers.map((s) => (
                        <div key={s.id} className="rounded-xl border border-slate-200 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900 truncate">{s.name || '—'}</div>
                              <div className="text-xs text-slate-500 truncate">{s.town || '—'}</div>
                            </div>
                            <div className="shrink-0">
                              {s.verified ? <Badge className="bg-green-500">{t('peso.badgeVerified', 'Verified')}</Badge> : <Badge variant="secondary">{t('peso.badgePending', 'Pending')}</Badge>}
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">{seekerActions(s)}</div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop: full table */}
                    <div className="hidden md:block">
                      <Table className="min-w-[560px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('peso.colName', 'Name')}</TableHead>
                            <TableHead>{t('peso.colTown', 'Town')}</TableHead>
                            <TableHead>{t('peso.colStatus', 'Status')}</TableHead>
                            <TableHead className="text-right">{t('peso.colAction', 'Action')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredSeekers.map((s) => (
                            <TableRow key={s.id}>
                              <TableCell className="font-medium">{s.name || '—'}</TableCell>
                              <TableCell>{s.town || '—'}</TableCell>
                              <TableCell>
                                {s.verified ? <Badge className="bg-green-500">{t('peso.badgeVerified', 'Verified')}</Badge> : <Badge variant="secondary">{t('peso.badgePending', 'Pending')}</Badge>}
                              </TableCell>
                              <TableCell className="text-right space-x-1 whitespace-nowrap">{seekerActions(s)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------------- DOLE Reports (SPRS) ---------------- */}
          <TabsContent value="reports" className="space-y-4">
            {/* The DOLE form builders. Hidden in demo mode, which has no staff token. */}
            {!demo && <PesoReportsForms authHeaders={authHeaders} officeName={analytics?.office?.name || profile?.peso_office || ''} />}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('peso.reportsTitle', 'DOLE SPRS Report')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <PesoFilterBar range={range} onRangeChange={setRange} activePreset={rPreset} onPresetChange={setRPreset}
                  events={events} selectedEvent={selectedEvent} onEventChange={setSelectedEvent} showEvents={events.length > 0} />
                <div>
                  <Button variant="outline" size="sm" onClick={loadSummary} disabled={summaryLoading}>
                    <RefreshCw size={14} className={`mr-1 ${summaryLoading ? 'animate-spin' : ''}`} /> {t('peso.refresh', 'Refresh')}
                  </Button>
                </div>

                {summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label={t('peso.sprsRegistered', 'Registered')} value={summary.registered?.total ?? 0}
                          sub={t('peso.sprsSexSplit', { m: summary.registered?.male ?? 0, f: summary.registered?.female ?? 0, defaultValue: `${summary.registered?.male ?? 0} M · ${summary.registered?.female ?? 0} F` })} />
                    <Stat label={t('peso.sprsVacancies', 'Vacancies solicited')} value={summary.vacancies?.postings ?? 0}
                          sub={t('peso.sprsPositions', { count: summary.vacancies?.positions ?? 0, defaultValue: `${summary.vacancies?.positions ?? 0} positions` })} />
                    <Stat label={t('peso.sprsReferred', 'Referred')} value={summary.referred ?? 0}
                          sub={t('peso.sprsReferredNote', 'portal applications')} />
                    <Stat label={t('peso.sprsPlaced', 'Placed')} value={summary.placed?.seekers ?? 0}
                          sub={t('peso.sprsPlacedNote', { count: summary.placed?.applications ?? 0, defaultValue: `${summary.placed?.applications ?? 0} hires` })} />
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {t('peso.sprsNote', 'Referred = applications submitted through the portal by your pre-screened (NSRP-registered) jobseekers. Placed = those subsequently hired. Per RA 8759, registration provides pre-screening and the portal application is the referral act.')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> {t('peso.exportsTitle', 'Excel Exports')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">{t('peso.exportsBody', 'Download office-scoped spreadsheets for the selected date range, ready for DOLE submission.')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    ['registrations', t('peso.exportRegistrations', 'Registrations (NSRP registry)')],
                    ['vacancies', t('peso.exportVacancies', 'Vacancies')],
                    ['referrals', t('peso.exportReferrals', 'Referrals')],
                    ['placements', t('peso.exportPlacements', 'Placements')],
                  ].map(([type, label]) => (
                    <Button key={type} variant="outline" className="justify-start" disabled={!!downloading} onClick={() => downloadReport(type)}>
                      {downloading === type ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
                      {label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------------- Events (SRA / job fairs) ---------------- */}
          <TabsContent value="events" className="space-y-4">
            <PesoEventsTab events={events} onReload={loadEvents} api={api} authHeaders={authHeaders} demo={demo} />
          </TabsContent>
        </Tabs>
      )}

      {/* Account: change password (reuses the app-wide, vetted PasswordChangeSection). */}
      <Dialog open={showPwDialog} onOpenChange={setShowPwDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] max-h-[90dvh] overflow-y-auto">
          <DialogTitle className="sr-only">{t('peso.changePassword', 'Change password')}</DialogTitle>
          <PasswordChangeSection profile={profile} />
        </DialogContent>
      </Dialog>
    </PesoLayout>
    </>
  );
};

export default PesoDashboard;
